// Draws the apples the simulation owns. Reads sim state, never mutates it
// (00-architecture.md §1); placement is the shared `placeOnSurface` the bug
// renderer uses, fed the apple's own frame (which interpolates its pivot).

import { type CookedModel, ModelRenderer, poseWorlds } from "../model";
import { multiply, type Mat4 } from "../math";
import { spawnerFrame, type Spawner, type SpawnerId } from "../game/spawners";
import { placeOnSurface } from "./bug-renderer";

/** Clip name and time for an apple's current state. */
interface Appearance {
  readonly clip: string;
  readonly time: number;
  readonly loop: boolean;
}

/** Clip duration, or 0 when the model has no such clip (`poseWorlds` rests). */
function duration(model: CookedModel, clip: string): number {
  return model.anims.get(clip)?.duration ?? 0;
}

/**
 * `time` is the wall clock, which only the looping bob needs — the one-shot
 * `create` clip is stretched over the sim's `SPAWN_ANIM_SECONDS` via the
 * state's own progress, so the sim keeps owning the timing.
 */
function appearance(s: Spawner, model: CookedModel, time: number): Appearance {
  const state = s.state;
  switch (state.kind) {
    case "moving":
    case "turning":
      // The apple bobs while it walks and while it pivots — it is never still.
      return { clip: "idle", time, loop: true };
    case "spawning":
      return { clip: "create", time: state.progress * duration(model, "create"), loop: false };
    default: {
      const unreachable: never = state;
      throw new Error(`unhandled spawner state ${JSON.stringify(unreachable)}`);
    }
  }
}

export class SpawnerRenderer {
  constructor(private readonly model: CookedModel) {}

  draw(
    gl: WebGL2RenderingContext,
    renderer: ModelRenderer,
    spawners: ReadonlyMap<SpawnerId, Spawner>,
    cubeModel: Mat4,
    viewProj: Mat4,
    time: number,
  ): void {
    for (const s of spawners.values()) {
      const look = appearance(s, this.model, time);
      renderer.draw(
        gl,
        this.model,
        poseWorlds(this.model, look.clip, look.time, look.loop),
        multiply(cubeModel, placeOnSurface(spawnerFrame(s), "apple", 1)),
        viewProj,
      );
    }
  }
}
